/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Link, type LinkProps, Skeleton, Typography2 } from '@coursera/cds-core';
import { Link as TrackedLink, type LinkProps as TrackedLinkProps } from '@coursera/event-pulse/react';

import {
  EmbeddedSourceCourseNameOptions,
  type EmbeddedSourceCourseNameOptionsType,
} from 'bundles/common/lms-authoring/types/EmbeddedSourceCourseNameOptions';
import type { SourceCoursePartners } from 'bundles/common/lms-authoring/types/SourceCoursePartners';

import _t from 'i18n!nls/common';

const styles = {
  name: css`
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  courseNameLink: css`
    color: var(--cds-color-grey-600) !important;
  `,
};

type Props = {
  partners: SourceCoursePartners;
  boldPartnerName: boolean;
  includeCourseName: boolean;
  shortFormContent: boolean;
  courseName?: string;
  courseSlug?: string | null;
  originalName?: string;
};

export const EmbeddedPartnerNameSkeleton = ({
  includeCourseName,
}: {
  includeCourseName?: EmbeddedSourceCourseNameOptionsType;
}) => (
  <Skeleton
    variant="text"
    width={includeCourseName === EmbeddedSourceCourseNameOptions.INLINE ? 300 : 100}
    css={styles.name}
  />
);

export const getPartnerName = (partners: SourceCoursePartners) => {
  const multiplePartners = partners.length > 1;
  return {
    multiplePartners,
    partnerName: multiplePartners ? _t('Multiple educators') : partners[0].name,
  };
};

export const PartnerNameText = ({
  partnerName,
  boldPartnerName,
}: {
  partnerName: string;
  boldPartnerName: boolean;
}) => {
  return boldPartnerName ? (
    <Typography2 variant="actionSecondary" component="span" color="supportText">
      {partnerName}
    </Typography2>
  ) : (
    partnerName
  );
};

export const TrackedStyledLink = (props: LinkProps & TrackedLinkProps) => {
  const { href, children, ...rest } = props;
  return (
    <TrackedLink css={styles.courseNameLink} target="_blank" rel="noopener noreferrer" href={href} {...rest}>
      {children}
    </TrackedLink>
  );
};

export const StyledLink = (props: LinkProps & { isTracked?: boolean }) => {
  const { href, children, ...rest } = props;
  return (
    <Link css={styles.courseNameLink} target="_blank" rel="noopener noreferrer" href={href} {...rest}>
      {children}
    </Link>
  );
};

export const CourseNameLink = ({ courseSlug, courseName }: { courseName?: string; courseSlug?: string | null }) => {
  if (courseSlug) {
    return (
      <StyledLink href={`/learn/${courseSlug}`} data-testid="course-name-link">
        {courseName}
      </StyledLink>
    );
  }

  return <React.Fragment>{courseName}</React.Fragment>;
};

const EmbeddedPartnerName = ({
  partners,
  boldPartnerName,
  includeCourseName,
  shortFormContent,
  courseName,
  courseSlug,
  originalName,
}: Props) => {
  const { partnerName, multiplePartners } = getPartnerName(partners);
  const partnerNameText = <PartnerNameText partnerName={partnerName} boldPartnerName={boldPartnerName} />;

  if (!includeCourseName) {
    return partnerNameText;
  }

  if (shortFormContent) {
    return <FormattedMessage message={_t('By {partnerName}')} partnerName={partnerName} />;
  }

  const courseNameLink = <CourseNameLink courseName={courseName} courseSlug={courseSlug} />;

  return (
    <React.Fragment>
      <FormattedMessage
        message={
          multiplePartners
            ? _t('From {courseNameLink} by multiple educators')
            : _t('From {courseNameLink} by {partnerName}')
        }
        courseNameLink={courseNameLink}
        partnerName={partnerName}
      />
      {originalName && _t(", originally titled '#{originalName}'", { originalName })}
    </React.Fragment>
  );
};

type TypographyProps = {
  className?: string;
};

export type InputProps = Props & TypographyProps;

export const EmbeddedPartnerNameTypography = ({ className, children }: React.PropsWithChildren<TypographyProps>) => (
  <Typography2
    variant="bodySecondary"
    component="span"
    color="supportText"
    data-testid="embedded-text-content"
    css={styles.name}
    className={className}
  >
    {children}
  </Typography2>
);

const EmbeddedPartnerNameWithTypography = ({ className, ...props }: React.PropsWithChildren<InputProps>) => (
  <EmbeddedPartnerNameTypography className={className}>
    <EmbeddedPartnerName {...props} />
  </EmbeddedPartnerNameTypography>
);

export default EmbeddedPartnerNameWithTypography;
